import React, { useMemo, useRef, useEffect } from 'react';
import cx from 'classnames';
import {
  StreamingEmbedTreeNodeData,
  EmbedType,
} from '@mutiny-pkg/champaign-schemas/src/schemas/flexiblePages/flexiblePagesTreeNode';
import { hydrateRichTextVariables, renderRichTextToPlainString } from '../../richtext/ast';
import { wrapContentWithAutoResize } from './embedAutoResize';

type EmbedProps = StreamingEmbedTreeNodeData & {
  id: string;
  editorClassName?: string;
  overrideVariables?: Partial<Record<string, string>>;
  isEditor: boolean;
  isDoubleClickActive?: boolean;
};

// srcdoc + sandbox without allow-same-origin isolates third-party HTML from the editor
// origin. A proxied /render/iframes?srcdoc= URL overflows request-line limits and 401s.
const EDITOR_HTML_EMBED_SANDBOX = 'allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox';

const SafeUrlEmbed = ({
  resolvedContent,
  isDoubleClickActive,
}: Pick<EmbedProps, 'isDoubleClickActive'> & { resolvedContent: string }) => {
  const iframeClassName = `border-none w-full h-full ${isDoubleClickActive ? '' : 'pointer-events-none'}`;

  return <iframe src={resolvedContent} title="Embedded content" loading="lazy" className={iframeClassName} />;
};

function isEmbedResizeMessage(data: unknown): data is { type: 'embed-resize'; height: number } {
  return (
    typeof data === 'object' &&
    data !== null &&
    'type' in data &&
    data.type === 'embed-resize' &&
    'height' in data &&
    typeof data.height === 'number' &&
    Number.isFinite(data.height)
  );
}

const SafeContentEmbed = ({
  resolvedContent,
  isDoubleClickActive,
}: Pick<EmbedProps, 'isDoubleClickActive'> & { resolvedContent: string }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const wrappedContentSnippet = useMemo(() => wrapContentWithAutoResize(resolvedContent), [resolvedContent]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) {
        return;
      }
      if (isEmbedResizeMessage(event.data) && iframeRef.current) {
        iframeRef.current.style.height = `${event.data.height}px`;
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const iframeClassName = `border-none w-full ${isDoubleClickActive ? '' : 'pointer-events-none'}`;

  return (
    <iframe
      ref={iframeRef}
      srcDoc={wrappedContentSnippet}
      title="Embedded content"
      loading="lazy"
      className={iframeClassName}
      sandbox={EDITOR_HTML_EMBED_SANDBOX}
    />
  );
};

const ContentEmbed = ({
  resolvedContent,
  isEditor,
  isDoubleClickActive,
}: Pick<EmbedProps, 'isEditor' | 'isDoubleClickActive'> & { resolvedContent: string }) => {
  if (isEditor) {
    return <SafeContentEmbed resolvedContent={resolvedContent} isDoubleClickActive={isDoubleClickActive} />;
  }

  return <div style={{ width: '100%', height: '100%' }} dangerouslySetInnerHTML={{ __html: resolvedContent }} />;
};

const hydrateAndRenderContent = (
  content: EmbedProps['url'] | EmbedProps['codeSnippet'],
  overrideVariables: EmbedProps['overrideVariables'],
) => {
  const contentData = content || '';
  const hydratedContent =
    typeof contentData !== 'string' ? hydrateRichTextVariables(contentData, overrideVariables || {}) : contentData;
  return typeof hydratedContent !== 'string' ? renderRichTextToPlainString(hydratedContent) : hydratedContent;
};

export const Embed = React.forwardRef<HTMLDivElement, EmbedProps>((embedProps, ref) => {
  const {
    id,
    className,
    editorClassName,
    embedType,
    url,
    codeSnippet,
    overrideVariables,
    isEditor,
    isDoubleClickActive,
    ...props
  } = embedProps;

  const resolvedContent = useMemo(() => {
    if (embedType === EmbedType.URL && url) {
      return hydrateAndRenderContent(url, overrideVariables);
    }
    if (embedType === EmbedType.Code && codeSnippet) {
      return hydrateAndRenderContent(codeSnippet, overrideVariables);
    }
    return '';
  }, [embedType, url, codeSnippet, overrideVariables]);

  return (
    <div id={id} ref={ref} className={cx(className, editorClassName)} {...props}>
      {embedType === EmbedType.URL ? (
        isEditor ? (
          <SafeUrlEmbed resolvedContent={resolvedContent} isDoubleClickActive={isDoubleClickActive} {...props} />
        ) : (
          <iframe
            src={resolvedContent}
            title="Embedded content"
            loading="lazy"
            style={{ border: 'none', width: '100%', height: '100%' }}
          />
        )
      ) : (
        <ContentEmbed
          resolvedContent={resolvedContent}
          isEditor={isEditor}
          isDoubleClickActive={isDoubleClickActive}
          {...props}
        />
      )}
    </div>
  );
});

Embed.displayName = 'Embed';
